import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogTitle,
  LinearProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { FetchError } from '../../../../common/api/api';
import { FileDto } from '../../Document.interface';
import {
  DocumentFolderUploadExists,
  DocumentFolderUploadInput,
  DocumentFolderUploadList,
  FileUploadManager,
  FileUploadProgress,
  useFileUploadManager
} from '.';

export interface DocumentFolderUploadComponentProps {
  folderName?: string;
  parentFolder?: FileDto;
  content?: React.ReactNode;
  actions?: React.ReactNode;
  onLoad?: (isLoading: boolean) => void;
  onError?: (error: FetchError) => void;
  onSelect?: (folder: FileDto | undefined, flag: boolean) => void;
}

export interface DocumentFolderUploadProps {
  parentFolder?: FileDto;
  asMultipleFiles?: boolean;
  folderInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  onCreateFolder?: (folder: FileDto) => void;
  onDismiss?: (progress: FileUploadProgress, folder?: FileDto) => void;
}

export const DocumentFolderUpload: React.FC<DocumentFolderUploadProps> = (
  props: DocumentFolderUploadProps
) => {
  const {
    parentFolder,
    asMultipleFiles = false,
    folderInputRef: folderInputRefProp,
    onDismiss,
    onCreateFolder
  } = props;
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const cancelCallbackRef = useRef<(() => void) | undefined>();
  const [open, setOpen] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [folder, setFolder] = useState<FileDto | undefined>();
  const [didFind, setDidFind] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [folderExists, setFolderExists] = useState<boolean>(false);
  const manager: FileUploadManager = useFileUploadManager();

  const folderName: string | undefined = useMemo(() => {
    const { files } = manager;
    if (!(files.length > 0)) {
      return;
    }
    const path: string = (files[0] as any).webkitRelativePath || '';
    const index: number = path.indexOf('/');
    return index > -1 ? path.slice(0, index) : '';
  }, [manager]);

  const dismiss = () => {
    cancelCallbackRef.current?.();
    setOpen(false);
    onDismiss?.(manager.progress, folder);
  };

  useLayoutEffect(() => {
    const el: any = folderInputRef.current;
    if (el) {
      (el as any).directory = !asMultipleFiles;
      (el as any).webkitdirectory = !asMultipleFiles;
    }
  }, [asMultipleFiles]);

  useEffect(() => {
    setOpen(manager.items.length > 0);
  }, [manager]);

  // clear state
  useEffect(() => {
    if (!open) {
      manager.clear();
      setFolder(undefined);
      setError(undefined);
      setDidFind(false);
      setIsLoading(false);
      setFolderExists(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismissButton: React.ReactNode = (
    <Button variant='outlined' onClick={dismiss}>
      Dismiss
    </Button>
  );

  const errorAlert: React.ReactNode = (
    <Collapse in={!!error} style={{ minHeight: 'unset' }}>
      <Box mx={3} mt={1}>
        <Alert severity='error'>{error?.message || 'An error occurred.'}</Alert>
      </Box>
    </Collapse>
  );

  return (
    <>
      <input
        hidden
        multiple
        type='file'
        ref={(ref: HTMLInputElement | null) => {
          folderInputRef.current = ref;
          if (folderInputRefProp) {
            folderInputRefProp.current = ref;
          }
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const { files } = event.target;
          if (files && files.length > 0) {
            manager.add(Array.from(files));
          }
          // clear value
          if (folderInputRef.current) {
            folderInputRef.current.value = '';
          }
        }}
      />

      <Dialog open={open} onClose={dismiss}>
        {isLoading && <LinearProgress />}
        <DialogTitle>Document Upload</DialogTitle>
        {open && !didFind && !folder && (
          <DocumentFolderUploadExists
            content={errorAlert}
            actions={dismissButton}
            folderName={folderName}
            parentFolder={parentFolder}
            onError={setError}
            onLoad={setIsLoading}
            onSelect={(folder: FileDto | undefined, folderExists: boolean) => {
              setDidFind(true);
              setFolder(folder);
              setFolderExists(folderExists);
            }}
          />
        )}
        {didFind && !folder && (
          <DocumentFolderUploadInput
            content={errorAlert}
            actions={dismissButton}
            folderName={folderName}
            parentFolder={parentFolder}
            createOnMount={!folderExists}
            onError={setError}
            onLoad={setIsLoading}
            onSelect={(folder: FileDto | undefined, created: boolean) => {
              setError(undefined);
              setFolder(folder);
              if (folder && created) {
                onCreateFolder?.(folder);
              }
            }}
          />
        )}
        {folder && (
          <>
            <DocumentFolderUploadList
              folder={folder}
              manager={manager}
              renderActions={({ progress, uploadAllButton, cancelAll }) => {
                cancelCallbackRef.current = cancelAll;
                return (
                  <>
                    {errorAlert}
                    <DialogActions>
                      <Button
                        variant='outlined'
                        onClick={dismiss}
                        disabled={progress.uploading > 0}
                      >
                        Dismiss
                      </Button>
                      {uploadAllButton}
                    </DialogActions>
                  </>
                );
              }}
            />
          </>
        )}
      </Dialog>
    </>
  );
};

export default DocumentFolderUpload;
